import { memo, useMemo, useState } from 'react';

import { VFlex, VStack, VTooltip, getStringToRGBA } from '@/package';
import type { Task, User } from '@/type';

export const TaskContributionSummary = memo(
  ({ taskDetail }: { taskDetail: Task }) => {
    const userTaskRatioMap = useMemo(() => {
      const map = new Map<
        string,
        { name: string; color: string; ratio: number; totalFrameCount: number }
      >();

      if (!taskDetail.userTask) return map;

      taskDetail.userTask.forEach(user => {
        const name = user.user.name || user.user.email.split('@')[0];

        map.set(user.userID, {
          color: getStringToRGBA(user.userID).toString(),
          name,
          ratio: +(
            (user.totalFrameCount / taskDetail.fullFrameCount) *
            100
          ).toFixed(2),
          totalFrameCount: user.totalFrameCount,
        });
      });

      return map;
    }, [taskDetail]);
    const [hoverUserID, setHoverUserID] = useState<Nullish<User['id']>>(null);

    return (
      <VStack gap={0.5}>
        <div>
          <strong className="text-body-xl text-secondary-500">
            Task contribution summary
          </strong>
        </div>

        <VFlex className="mt-1 h-4 w-full divide-x divide-gray-200 rounded-full">
          {[...userTaskRatioMap].map(
            ([userID, { color, name, ratio, totalFrameCount }]) => {
              return (
                <VTooltip
                  content={() => {
                    return (
                      <VStack
                        className="-mx-[9px] -my-[5px] box-content w-[107px] rounded-sm border border-tertiary-400 bg-white p-scale/8 text-body-md text-gray-600"
                        gap={0.125}
                      >
                        <div className="overflow-hidden font-semibold text-ellipsis whitespace-nowrap">
                          {name}
                        </div>
                        <VFlex items="center" gap={0.25}>
                          <div className="flex-1 text-right">
                            {Intl.NumberFormat().format(totalFrameCount)}
                          </div>
                          <div className="h-2.5 w-px bg-gray-300">&nbsp;</div>
                          <div>{ratio}%</div>
                        </VFlex>
                      </VStack>
                    );
                  }}
                  placement="bottom"
                >
                  <div
                    key={userID}
                    className="h-full cursor-pointer bg-(--bg-color) opacity-(--opacity) first:rounded-l-full last:rounded-r-full"
                    style={
                      {
                        '--bg-color': color,
                        '--opacity':
                          hoverUserID == null || hoverUserID === userID
                            ? 1
                            : 0.5,
                        width: `${ratio}%`,
                      } as React.CSSProperties
                    }
                    onMouseEnter={() => setHoverUserID(userID)}
                    onMouseLeave={() => setHoverUserID(null)}
                  >
                    &nbsp;
                  </div>
                </VTooltip>
              );
            },
          )}
        </VFlex>

        <VFlex
          wrap="wrap"
          className="gap-x-scale/12 gap-y-scale/4 text-body-md text-gray-500"
        >
          {[...userTaskRatioMap].map(
            ([userID, { name, color, ratio, totalFrameCount }]) => {
              return (
                <VFlex
                  key={userID}
                  items="center"
                  data-testid="task-contribution-summary-label-user-name"
                  gap={0.25}
                >
                  <div
                    className="inline-block size-2 rounded-full bg-(--bg-color)"
                    style={
                      {
                        '--bg-color': color,
                      } as React.CSSProperties
                    }
                  ></div>
                  <div className="max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">
                    {name}
                  </div>
                  <div>{Intl.NumberFormat().format(totalFrameCount)}</div>
                  <div className="h-3 w-px bg-gray-300">&nbsp;</div>
                  <div className="font-semibold">{ratio}%</div>
                </VFlex>
              );
            },
          )}
        </VFlex>
      </VStack>
    );
  },
  (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.taskDetail?.userTask) ===
      JSON.stringify(nextProps.taskDetail?.userTask)
    );
  },
);

TaskContributionSummary.displayName = 'TaskContributionSummary';
